import { HttpClient, HttpParams, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { catchError, finalize, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Event } from "../domain/event";
import { Intervention } from "../domain/intervention";
import { User } from "../domain/user";
import { CoreData } from "../tools/core-data/core-data";
import { mapper } from "../tools/mapping/mapper";
import { LoadingTask } from "../tools/ui/loading-task";
import { BaseService } from "./base.service";
import { EventDTO } from "./dto/event.dto";
import { InterventionDTO } from "./dto/intervention.dto";
import { SamplerCounterDto } from "./dto/sampler-counter";
import { PlanInterventionDTO } from "./plan-intervention.dto";
import { SamplersDTO } from "./dto/samplers.dto";

@Injectable({
    providedIn: 'root',
})
export class InterventionService extends BaseService {

    private serviceUrl = `${environment.serviceBaseUrl}api/intervention`;

    constructor(public http: HttpClient,
        protected override coreData: CoreData,
        protected snackBare: MatSnackBar,
        private loadingTask: LoadingTask,
        protected override router: Router) {

        super(router, coreData, snackBare);
    }

    getInterventionListByDate(date: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/my/${date}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getInterventionListByDate')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: EventDTO[]) => {
                return response.map((item: EventDTO) => {
                    return mapper.map(item, EventDTO, Event);
                });
            }));
    }

    getInterventionByCode(code: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${code}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getInterventionByCode')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: InterventionDTO) => {
                return Intervention.mapFromJSON(response);
            }));
    }

    getUnplannedList(analyticalUnitCode: string, date: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${analyticalUnitCode}/${date}/unplanned`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getUnplannedList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: InterventionDTO[]) => {
                return response.map((item: InterventionDTO) => {
                    return mapper.map(item, InterventionDTO, Intervention);
                });
            }));
    }

    saveEvent(interventionCode: string, samplerPuid: string, dateTimeString: string) {
        this.loadingTask.activateLoading();
        const dto = new PlanInterventionDTO();
        dto.SamplerPuid = samplerPuid;
        dto.InterventionCode = interventionCode;
        dto.Date = dateTimeString;
        // return this.http.put(`${this.serviceUrl}/planning`, dto, this.generateHeader()).pipe(
        //     catchError(this.handleError<any>('saveEvent')),
        //     finalize(() => this.loadingTask.deactivateLoading()),
        //     map((response: string) => {
        //         return response;
        //     }));
        return this.http.request(new HttpRequest('PUT', `${this.serviceUrl}/planning`, dto, this.generateHeader()));
    }

    getCounters(puid: string, analyticalUnitCode: string, date: string, isDepose: boolean) {
        this.loadingTask.activateLoading();
        let params = new HttpParams();
        params = params.append('isDepose', `${isDepose}`);

        return this.http.get(`${this.serviceUrl}/operator/${puid}/${date}/${analyticalUnitCode}`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getCounters')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: SamplerCounterDto) => {
                return response;
            }));
    }

    getCountersForSamplerList(puidList: string[], analyticalUnitCode: string, date: string) {
        this.loadingTask.activateLoading();
        // let params = new HttpParams();
        // params = params.append('isDepose', `${isDepose}`);
        let obj = new SamplersDTO();
        obj.SamplerPuidList = puidList;
        return this.http.post(`${this.serviceUrl}/operator/compteurs/${date}/${analyticalUnitCode}`, obj, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getCountersForSamplerList')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: SamplerCounterDto[]) => {
                return response.map((item: SamplerCounterDto) => {
                    return item;
                });
            }));
    }

    getGlobalCounters(analyticalUnitCode: string, date: string, isDepose: boolean) {
        this.loadingTask.activateLoading();
        let params = new HttpParams();
        params = params.append('isDepose', `${isDepose}`);

        return this.http.get(`${this.serviceUrl}/agency/${date}/${analyticalUnitCode}`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getGlobalCounters')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: SamplerCounterDto) => {
                return response;
            }));
    }

    generateAutoPlan(analyticalUnitCode: string, date: string, userList: User[]) {
        this.loadingTask.activateLoading();
        let params = new HttpParams();
        params = params.append('firstSolutionStrategy', 8);
        params = params.append('maxSolutionTime', 2);
        userList.forEach(u => {
            params = params.append('sampler', `${u.Puid}`);
        })
        return this.http.post(`${this.serviceUrl}/autoplan/${analyticalUnitCode}/${date}`, null, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('savegenerateAutoPlanEvent')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }
}